import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider
} from "@mui/material";

/**
 * Recorte con aspect ratio fijo (sin selector: el destino en la app siempre
 * es un círculo/cuadrado, no tiene sentido ofrecer otra proporción) +
 * zoom/pan táctil, para que el admin pueda elegir el mismo tipo de punto
 * focal que Instagram deja elegir al subir una foto de perfil — ver
 * auditoría de imágenes de Tablados, 2026-09. Devuelve un `File` con el
 * mismo nombre/`type` que el original (solo cambia contenido/dimensiones),
 * para no romper el contrato que ya espera cada caller de
 * `ImageUploadField`.
 */
async function recortarComoFile(file, croppedAreaPixels) {
  const imagenOriginal = await cargarImagen(file);
  const canvas = document.createElement("canvas");
  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    imagenOriginal,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height
  );

  const blob = await new Promise(resolve =>
    canvas.toBlob(resolve, file.type || "image/jpeg", 0.92)
  );
  return new File([blob], file.name, { type: blob.type });
}

function cargarImagen(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export const ImageCropDialog = ({ open, file, aspectRatio, onCancel, onCropped }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [procesando, setProcesando] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleAplicar = async () => {
    if (!file || !croppedAreaPixels) return;
    setProcesando(true);
    try {
      const archivoRecortado = await recortarComoFile(file, croppedAreaPixels);
      onCropped(archivoRecortado);
    } finally {
      setProcesando(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Ajustar imagen</DialogTitle>
      <DialogContent>
        <Box sx={{ position: "relative", width: "100%", height: 320, bgcolor: "#000" }}>
          {file && (
            <Cropper
              image={URL.createObjectURL(file)}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>
        <Box sx={{ px: 2, pt: 2 }}>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_e, value) => setZoom(value)}
            aria-label="Zoom"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={procesando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleAplicar} disabled={procesando}>
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
